import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { setPassword } from "../api/account";
import { Button } from "@t-one-internal/design-system";
import { Input } from "@t-one-internal/design-system";
import { Label } from "@t-one-internal/design-system";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@t-one-internal/design-system";
import { Eye, EyeOff } from "lucide-react";

export default function SetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<{
    score: number;
    feedback: string;
    color: string;
  }>({ score: 0, feedback: "", color: "bg-gray-200" });
  const [validationErrors, setValidationErrors] = useState({
    minLength: false,
    maxLength: true,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecialChar: false,
    noRepeatedChars: true,
    noSequentialChars: true,
  });

  useEffect(() => {
    if (!token) {
      setError("Invalid or missing token. Please check your email link.");
    }
  }, [token]);

  const validatePassword = (password: string) => {
    // Check for 3+ repeated identical characters
    const hasRepeated = /(.)\1{2,}/.test(password);
    // Check for 3+ sequential ascending/descending characters
    const hasSequential = (() => {
      for (let i = 0; i < password.length - 2; i++) {
        const a = password.charCodeAt(i);
        const b = password.charCodeAt(i + 1);
        const c = password.charCodeAt(i + 2);
        if ((b - a === 1 && c - b === 1) || (a - b === 1 && b - c === 1)) {
          return true;
        }
      }
      return false;
    })();

    const validations = {
      minLength: password.length >= 12,
      maxLength: password.length <= 128,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
      noRepeatedChars: !hasRepeated,
      noSequentialChars: !hasSequential,
    };

    setValidationErrors(validations);

    const passedValidations = Object.values(validations).filter(Boolean).length;
    const totalValidations = Object.keys(validations).length;
    let strength = { score: 0, feedback: "", color: "bg-gray-200" };

    if (password.length === 0) {
      strength = { score: 0, feedback: "", color: "bg-gray-200" };
    } else if (passedValidations <= 3) {
      strength = { score: 25, feedback: "Weak", color: "bg-red-500" };
    } else if (passedValidations <= 5) {
      strength = { score: 50, feedback: "Fair", color: "bg-orange-500" };
    } else if (passedValidations <= 7) {
      strength = { score: 75, feedback: "Good", color: "bg-yellow-500" };
    } else {
      strength = { score: 100, feedback: "Strong", color: "bg-green-500" };
    }

    setPasswordStrength(strength);
    return Object.values(validations).every(Boolean);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setFormData({ ...formData, password: newPassword });
    validatePassword(newPassword);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!token) {
      setError("Invalid or missing token. Please check your email link.");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (!validatePassword(formData.password)) {
      setError("Password does not meet all requirements");
      return;
    }

    setIsSubmitting(true);

    try {
      // The backend expects token and password in the request body
      // The token contains the email and will be validated by the backend
      const response = await setPassword({
        token,
        password: formData.password,
      });

      console.log("Password set successfully", response);

      // Show success message and redirect to login
      navigate("/login", {
        state: {
          message: "Password has been set successfully! You can now login with your new password.",
        },
      });
    } catch (error: any) {
      console.error("Error setting password:", error);
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.response?.data?.detail ||
        "Failed to set password. The link may have expired. Please try registering again.";
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-100 p-4">
      <Card className="w-full max-w-md shadow-xl border-0">
        <CardHeader className="space-y-1 pb-8">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#4B45E5' }}>
              <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-center text-gray-900">
            {token ? 'Set Your Password' : 'Invalid Link'}
          </CardTitle>
          <CardDescription className="text-center text-gray-600">
            {token ? 'Create a secure password for your account' : 'Please check your email for a valid link'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handlePasswordChange}
                  className="h-11 pr-10"
                  required
                  disabled={isSubmitting || !token}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>

              {formData.password && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">Password strength</span>
                    <span className="font-medium text-gray-700">{passwordStrength.feedback}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${passwordStrength.color}`}
                      style={{ width: `${passwordStrength.score}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">
                Confirm Password
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className="h-11 pr-10"
                  required
                  disabled={isSubmitting || !token}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {formData.password && (
              <div className="bg-gray-50 border border-gray-200 rounded-md p-4 space-y-2">
                <p className="text-xs font-medium text-gray-700 mb-2">Password must contain:</p>
                <div className="space-y-1">
                  {([
                    { key: 'minLength' as const, label: 'At least 12 characters' },
                    { key: 'maxLength' as const, label: 'No more than 128 characters' },
                    { key: 'hasUppercase' as const, label: 'One uppercase letter' },
                    { key: 'hasLowercase' as const, label: 'One lowercase letter' },
                    { key: 'hasNumber' as const, label: 'One number' },
                    { key: 'hasSpecialChar' as const, label: 'One special character' },
                    { key: 'noRepeatedChars' as const, label: 'No 3+ repeated characters (e.g., aaa)' },
                    { key: 'noSequentialChars' as const, label: 'No 3+ sequential characters (e.g., abc, 123)' },
                  ]).map(({ key, label }) => (
                    <div key={key} className="flex items-center text-xs">
                      <span className={`mr-2 ${validationErrors[key] ? 'text-green-600' : 'text-gray-400'}`}>
                        {validationErrors[key] ? '✓' : '○'}
                      </span>
                      <span className={validationErrors[key] ? 'text-gray-700' : 'text-gray-500'}>
                        {label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-11"
              style={{ backgroundColor: '#4B45E5', '--hover-color': '#3B35D5' }}
              disabled={isSubmitting || !token}
            >
              {isSubmitting ? "Setting Password..." : "Set Password"}
            </Button>
          </form>

          <div className="text-center text-sm text-gray-600">
            Remember your password?{" "}
            <button
              onClick={() => navigate("/login")}
              className="hover:underline font-medium"
              style={{ color: '#4B45E5' }}
            >
              Sign in here
            </button>
          </div>
        </CardContent>
      </Card>
    </div >
  );
}
